import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { DownloadIcon, FileSignatureIcon, Loader2Icon, ArrowLeftIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { validationProofsApi } from '@/api/validation-proofs'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { ReturnLink } from '@/components/return-link'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PROOFS_KEY } from '@/components/validations/team-proof-badge'
import { downloadDocument, useDocumentContent } from '@/hooks/use-document-content'
import { formatDateTime } from '@/lib/format'
import { RETURN_SHAPE } from '@/lib/return-to'
import { readSearch } from '@/lib/search-params'

export const Route = createFileRoute('/_app/validations/$shortId')({
  // #401 : « Retour » ramène là d'où la preuve a été ouverte.
  validateSearch: (search: Record<string, unknown>) => readSearch(RETURN_SHAPE, search),
  component: ValidationProofPage,
})

const KIND_LABELS = {
  spec_section: 'Section du cahier',
  spec_chapter: 'Chapitre du cahier',
  delivery: 'Recette acceptée',
  quote: 'Soumission acceptée',
} as const

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-0.5 py-1.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm break-words">{children}</dd>
    </div>
  )
}

/**
 * Une preuve de validation signée (lot 31), côté équipe — le lien des
 * courriels : ce qui a été validé, la phrase signée, le signataire, l'heure,
 * l'adresse IP et le navigateur, l'empreinte, et le PDF figé.
 */
function ValidationProofPage() {
  const { shortId } = Route.useParams()
  const proof = useQuery({
    queryKey: [...PROOFS_KEY, 'detail', shortId],
    queryFn: () => validationProofsApi.get(shortId),
  })
  const pdf = useDocumentContent(proof.data ? validationProofsApi.pdfPath(proof.data.id) : null)

  if (proof.isPending) return <Skeleton className="h-64 w-full rounded-xl" />
  if (proof.isError) {
    return (
      <FormError>
        {proof.error instanceof ApiError ? proof.error.message : 'Preuve introuvable.'}
      </FormError>
    )
  }
  const data = proof.data

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow={
          <ReturnLink
            names={{ client: data.client.name }}
            className="-ml-2"
            fallback={
              <Button asChild variant="ghost" size="sm" className="-ml-2">
                <Link to="/clients/$clientId" params={{ clientId: data.client_id }}>
                  <ArrowLeftIcon />
                  Retour à {data.client.name}
                </Link>
              </Button>
            }
          />
        }
        title={
          <span className="flex flex-wrap items-center gap-2">
            <FileSignatureIcon className="size-5" aria-hidden="true" />
            Preuve <span className="font-key">{data.short_id}</span>
          </span>
        }
        documentTitle={`Preuve ${data.short_id}`}
        description={`${KIND_LABELS[data.kind]} · ${data.object_label}`}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void downloadDocument(
                validationProofsApi.pdfPath(data.id),
                `preuve-${data.short_id}.pdf`,
              ).catch(() => {
                toast.error('Téléchargement impossible.')
              })
            }}
          >
            <DownloadIcon />
            Télécharger le PDF
          </Button>
        }
      />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Frame>
          <dl className="divide-y" data-testid="preuve-detail">
            <Row label="Phrase signée">« {data.statement} »</Row>
            <Row label="Nom tapé">{data.signed_name}</Row>
            <Row label="Signataire">
              {data.signer.full_name} · {data.signer_email}
            </Row>
            <Row label="Client">
              <Link
                to="/clients/$clientId"
                params={{ clientId: data.client_id }}
                className="text-primary hover:underline"
              >
                {data.client.name}
              </Link>
              {data.project && ` · ${data.project.name}`}
            </Row>
            <Row label="Signée le">{formatDateTime(data.signed_at)}</Row>
            <Row label="Adresse IP">{data.ip}</Row>
            <Row label="Navigateur">{data.user_agent}</Row>
            <Row label="Empreinte">
              <span className="font-mono text-xs break-all">{data.content_sha256}</span>
            </Row>
            {data.items.length > 1 && (
              <Row label="Sections">
                <ul className="space-y-1">
                  {data.items.map((item) => (
                    <li key={item.object_id}>
                      {item.label}
                      {item.revision !== null && ` · révision ${String(item.revision)}`}
                    </li>
                  ))}
                </ul>
              </Row>
            )}
          </dl>
        </Frame>
        <Frame>
          {pdf.url ? (
            <iframe src={pdf.url} title={`Preuve ${data.short_id}`} className="h-[70dvh] w-full" />
          ) : pdf.error ? (
            <p className="text-sm text-muted-foreground">
              Le PDF ne s’affiche pas ici : téléchargez-le.
            </p>
          ) : (
            <div className="flex h-48 items-center justify-center">
              <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </Frame>
      </div>
    </div>
  )
}
